// AIImportWizard — Beyond AI converts a Word/PDF/text form into a Priority Risk
// draft template, then opens it in the FormBuilder for review before publishing.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Upload, FileText, CheckCircle, Edit, AlertTriangle, Sparkles, RefreshCw,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
    { id: 1, label: "Upload document" },
    { id: 2, label: "Beyond AI analysis" },
    { id: 3, label: "Review draft" },
];

const INTERNAL_ROLES = new Set(["director", "administrator", "consultant"]);

export default function AIImportWizard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isInternal = user && INTERNAL_ROLES.has(user.role);
    const [step, setStep] = useState(1);
    const [file, setFile] = useState(null);
    const [textInput, setTextInput] = useState("");
    const [mode, setMode] = useState("file");
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isInternal) api.get("/clients").then((r) => setClients(r.data)).catch(() => { });
    }, [isInternal]);

    async function analyse() {
        if (mode === "file" && !file) return toast.error("Please choose a file");
        if (mode === "text" && !textInput.trim()) return toast.error("Please paste some text");
        setLoading(true);
        setError(null);
        setStep(2);
        try {
            const fd = new FormData();
            if (mode === "file") fd.append("file", file);
            else fd.append("text_input", textInput);
            if (isInternal && clientId) fd.append("client_id", clientId);
            const { data } = await api.post("/forms/templates/ai-import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            setStep(3);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Beyond AI couldn't analyse this document. Please try again.");
            setStep(1);
        } finally {
            setLoading(false);
        }
    }

    const sections = result?.template?.sections || [];

    return (
        <div data-testid="ai-import-wizard" className="space-y-5">
            {/* Header */}
            <div className="flex items-center gap-3">
                <button onClick={() => navigate("/app/templates")} data-testid="ai-import-back"
                    className="p-1.5 rounded-lg hover:bg-[#F0EDE8] text-[#6F6A64]">
                    <ArrowLeft size={18} />
                </button>
                <div className="w-9 h-9 rounded-[10px] bg-ai-grad grid place-items-center text-white">
                    <Sparkles size={18} />
                </div>
                <div className="flex-1">
                    <h2 className="text-xl font-bold tracking-tight text-[#1F1F1F]">Import with Beyond AI</h2>
                    <p className="text-[12.5px] text-[#6F6A64]">Upload a Word or PDF form — Beyond AI drafts a template for you to review.</p>
                </div>
                {/* Step progress */}
                <div className="hidden md:flex items-center gap-2">
                    {STEPS.map((s, i) => (
                        <div key={s.id} className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold ${step > s.id ? "bg-emerald-500 text-white" : step === s.id ? "bg-ai-grad text-white" : "bg-[#E5E0DA] text-[#9A968F]"}`}>
                                {step > s.id ? <CheckCircle size={12} /> : s.id}
                            </div>
                            <span className={`text-[12px] ${step === s.id ? "font-semibold text-[#1F1F1F]" : "text-[#9A968F]"}`}>{s.label}</span>
                            {i < STEPS.length - 1 && <div className="w-5 h-px bg-[#E5E0DA]" />}
                        </div>
                    ))}
                </div>
            </div>

            <div className="max-w-2xl mx-auto w-full">
                {/* Step 1 — Upload */}
                {step === 1 && (
                    <div className="space-y-5">
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-2xl bg-ai-grad grid place-items-center mx-auto mb-3 text-white">
                                <Sparkles size={32} />
                            </div>
                            <h3 className="text-lg font-bold text-[#1F1F1F]">Import with Beyond AI</h3>
                            <p className="text-[13px] text-[#6F6A64] mt-1">
                                Upload a construction or compliance form — Beyond AI reads the structure and drafts a template alongside you.
                            </p>
                        </div>

                        {error && (
                            <div data-testid="ai-import-error" className="p-3.5 rounded-xl border border-red-200 bg-red-50 flex gap-2.5">
                                <AlertTriangle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                                <p className="text-[13px] text-red-700">{error}</p>
                            </div>
                        )}

                        {/* Scope — internal staff can draft a global master or upload on behalf of a specific client */}
                        {isInternal && (
                            <div>
                                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#6F6A64] mb-1.5">For</label>
                                <select data-testid="ai-import-client" value={clientId} onChange={(e) => setClientId(e.target.value)}
                                    className="w-full h-10 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[#1F6FE5]">
                                    <option value="">Global master (all clients)</option>
                                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name} (on their behalf)</option>)}
                                </select>
                            </div>
                        )}

                        {/* Mode toggle */}
                        <div className="flex gap-2 p-1 bg-[#F0EDE8] rounded-xl w-fit mx-auto">
                            {[["file", "Upload file"], ["text", "Paste text"]].map(([m, label]) => (
                                <button key={m} onClick={() => setMode(m)} data-testid={`ai-import-mode-${m}`}
                                    className={`px-4 py-1.5 rounded-lg text-[13px] font-semibold transition-all ${mode === m ? "bg-white shadow-sm text-[#1F1F1F]" : "text-[#6F6A64]"}`}>
                                    {label}
                                </button>
                            ))}
                        </div>

                        {mode === "file" ? (
                            <label data-testid="ai-import-dropzone"
                                className={`block border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all ${file ? "border-emerald-300 bg-emerald-50/40" : "border-[#D8D3CB] hover:border-[#9A968F] bg-white"}`}>
                                <input type="file" accept=".docx,.doc,.pdf,.txt" className="sr-only"
                                    data-testid="ai-import-file"
                                    onChange={(e) => setFile(e.target.files?.[0] || null)} />
                                {file ? (
                                    <div>
                                        <FileText size={30} className="mx-auto mb-2.5 text-emerald-500" />
                                        <p className="font-semibold text-[#1F1F1F]">{file.name}</p>
                                        <p className="text-[12px] text-[#9A968F] mt-1">{(file.size / 1024).toFixed(1)} KB · click to change</p>
                                    </div>
                                ) : (
                                    <div>
                                        <Upload size={30} className="mx-auto mb-2.5 text-[#C4BEB4]" />
                                        <p className="font-semibold text-[#1F1F1F]">Drop file here or click to browse</p>
                                        <p className="text-[12px] text-[#9A968F] mt-1">Supports: .docx, .doc, .pdf, .txt</p>
                                    </div>
                                )}
                            </label>
                        ) : (
                            <div>
                                <p className="text-[13px] text-[#6F6A64] mb-1.5">Paste the form content or template text:</p>
                                <textarea data-testid="ai-import-text" value={textInput} rows={10}
                                    onChange={(e) => setTextInput(e.target.value)}
                                    placeholder="Paste the full text of your form here. Include section headings, field names, YES/NO/N/A tables, signature blocks etc…"
                                    className="w-full p-3 rounded-xl border border-[#E5E0DA] bg-white text-[13px] font-mono focus:outline-none focus:border-[#1F6FE5]" />
                                <p className="text-[11px] text-[#9A968F] mt-1">{textInput.length} characters</p>
                            </div>
                        )}

                        <div className="p-4 rounded-xl bg-ai-grad-soft border border-[#BFD8F5]">
                            <p className="text-[12px] font-bold mb-1.5" style={{ color: "#1F6FE5" }}>How Beyond AI helps</p>
                            <ul className="text-[12px] space-y-1" style={{ color: "#1E5FCC" }}>
                                <li>• Reads document structure and detects field types</li>
                                <li>• YES/NO/N/A tables → individual Yes / No / N/A checks</li>
                                <li>• Signature blocks → Signature fields</li>
                                <li>• Auto-detects project / client / date fields</li>
                                <li>• Drafts a template — you review before publishing</li>
                            </ul>
                        </div>

                        <button onClick={analyse} data-testid="ai-import-analyse"
                            className="w-full h-12 rounded-xl text-white font-semibold inline-flex items-center justify-center gap-2 bg-ai-grad">
                            <Sparkles size={16} /> Analyse with Beyond AI
                        </button>
                    </div>
                )}

                {/* Step 2 — Processing */}
                {step === 2 && loading && (
                    <div className="text-center py-16">
                        <div className="w-16 h-16 rounded-2xl bg-ai-grad grid place-items-center mx-auto mb-5 text-white">
                            <Sparkles size={32} className="animate-pulse" />
                        </div>
                        <h3 className="text-lg font-bold text-[#1F1F1F] mb-1.5">Beyond AI is analysing…</h3>
                        <p className="text-[13px] text-[#6F6A64] mb-5">Reading the document structure and drafting your template. Usually 10–20 seconds.</p>
                        <div className="flex justify-center gap-1.5">
                            {[0, 1, 2].map((i) => (
                                <div key={i} className="w-2 h-2 rounded-full animate-bounce"
                                    style={{ background: "#2DD4BF", animationDelay: `${i * 0.15}s` }} />
                            ))}
                        </div>
                    </div>
                )}

                {/* Step 3 — Review */}
                {step === 3 && result && (
                    <div className="space-y-4" data-testid="ai-import-result">
                        <div className="flex items-center gap-3 p-4 rounded-xl border border-emerald-200 bg-emerald-50">
                            <CheckCircle size={20} className="text-emerald-600 flex-shrink-0" />
                            <div>
                                <p className="text-[13px] font-bold text-emerald-800">Draft template generated</p>
                                <p className="text-[12px] text-emerald-600 mt-0.5">
                                    Read {result.extracted_chars?.toLocaleString()} characters · review every section before publishing.
                                </p>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl border border-[#E5E0DA] p-5 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                {[
                                    ["Template name", result.template.name],
                                    ["Code", result.template.code],
                                    ["Category", result.template.category],
                                    ["Signature", result.template.requires_signature ? "Required" : "Not required"],
                                    ["Sections", `${result.section_count} sections`],
                                    ["Fields", `${result.field_count} fields`],
                                ].map(([label, value]) => (
                                    <div key={label}>
                                        <p className="text-[10.5px] text-[#9A968F] uppercase tracking-wider">{label}</p>
                                        <p className="text-[13px] font-semibold text-[#1F1F1F] mt-0.5 capitalize">{value}</p>
                                    </div>
                                ))}
                            </div>

                            <div>
                                <p className="text-[10.5px] font-bold text-[#6F6A64] uppercase tracking-wider mb-2">Detected sections</p>
                                <div className="space-y-2">
                                    {sections.map((sec, i) => (
                                        <div key={sec.id} className="flex items-center justify-between p-2.5 bg-[#F5F3F0] rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <span className="w-5 h-5 rounded text-[11px] font-bold grid place-items-center text-white bg-ai-grad">{i + 1}</span>
                                                <span className="text-[13px] font-semibold text-[#1F1F1F]">{sec.title}</span>
                                            </div>
                                            <span className="text-[12px] text-[#9A968F]">{(sec.fields || []).length} fields</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
                            <p className="text-[12px] font-bold text-amber-700 mb-0.5">This is a DRAFT</p>
                            <p className="text-[12px] text-amber-600">It will NOT be published until you review and publish it in the builder. Please check all sections and fields carefully.</p>
                        </div>

                        <div className="flex gap-3">
                            <button onClick={() => { setStep(1); setResult(null); }} data-testid="ai-import-try-again"
                                className="flex-1 h-11 rounded-xl border border-[#E5E0DA] font-semibold text-[#1F1F1F] hover:bg-[#F6F4F0] inline-flex items-center justify-center gap-2">
                                <RefreshCw size={14} /> Try again
                            </button>
                            <button onClick={() => navigate(`/app/templates/${result.template.id}`)} data-testid="ai-import-open-builder"
                                className="flex-1 h-11 rounded-xl text-white font-semibold inline-flex items-center justify-center gap-2"
                                style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                <Edit size={14} /> Open in builder →
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
